import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Copy,
  KeyRound,
  MoreHorizontal,
  Plus,
  RefreshCcw,
  ShieldOff,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { authHeaders } from "@/lib/authToken";
import { formatDistanceToNow } from "date-fns";

/* ------------------------------------------------------------------ */
/* API client                                                          */
/* ------------------------------------------------------------------ */

type ApiKeyRecord = {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  status: "active" | "revoked";
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string | null;
};

async function v1Fetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`/api/v1${path}`, { ...init, headers: { ...headers, ...init?.headers } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

const SCOPE_PRESETS: Record<string, { label: string; scopes: string[] }> = {
  full: { label: "Full access (all resources)", scopes: ["*"] },
  readonly: {
    label: "Read-only (all resources)",
    scopes: [
      "chat:read",
      "workspaces:read",
      "spaces:read",
      "topics:read",
      "documents:read",
      "agents:read",
      "plugins:read",
    ],
  },
  chat: { label: "Chat only", scopes: ["chat:write"] },
  knowledge: {
    label: "Knowledge only (spaces, topics, documents)",
    scopes: ["spaces:write", "topics:write", "documents:write"],
  },
  plugins: { label: "Plugins only", scopes: ["plugins:write"] },
};

/* ------------------------------------------------------------------ */
/* Documentation content                                               */
/* ------------------------------------------------------------------ */

type EndpointDoc = { method: string; path: string; description: string };
type ResourceDoc = {
  title: string;
  blurb: string;
  endpoints: EndpointDoc[];
  example?: string;
  response?: string;
};

const DOCS: ResourceDoc[] = [
  {
    title: "Authentication",
    blurb:
      'Send your API key with every request: "Authorization: Bearer dsk_…" (or "X-Api-Key: dsk_…"). Keys carry scopes — "*" grants every resource except key management; per-resource scopes look like "chat:read" or "documents:write" (write implies read). The plaintext key is shown once at creation. Errors always use { "error": { "code", "message" } }. Lists accept ?limit= (1–200) and ?offset=.',
    endpoints: [{ method: "GET", path: "/api/v1", description: "API index (no auth required)" }],
    example: `curl https://www.deeplysmart.app/api/v1/spaces \\\n  -H "Authorization: Bearer dsk_YOUR_KEY"`,
    response: `{
  "data": [
    {
      "id": "mkvo05r2c6ebeua",
      "name": "Product Launch",
      "description": "Q3 launch plan",
      "icon": null,
      "color": null,
      "goals": null,
      "notes": null,
      "workspaceId": null,
      "includeInRag": true,
      "createdAt": "2026-07-17T18:54:08.183Z",
      "updatedAt": "2026-07-17T18:54:08.183Z"
    }
  ],
  "limit": 50,
  "offset": 0
}`,
  },
  {
    title: "API Keys",
    blurb:
      'Manage keys programmatically. Requires a session token or a key with the explicit "keys:write" scope — "*" deliberately excludes key management.',
    endpoints: [
      { method: "GET", path: "/api/v1/keys", description: "List keys" },
      { method: "POST", path: "/api/v1/keys", description: 'Create — { "name", "scopes"?, "expiresAt"? }; response includes "key" once' },
      { method: "GET", path: "/api/v1/keys/:id", description: "Key detail" },
      { method: "PATCH", path: "/api/v1/keys/:id", description: "Update name, scopes, expiresAt, or status (active/revoked)" },
      { method: "DELETE", path: "/api/v1/keys/:id", description: "Delete permanently" },
    ],
    example: `curl -X POST $BASE/keys -H "Authorization: Bearer $SESSION_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"CI key","scopes":["chat:write","documents:read"]}'`,
    response: `{
  "data": {
    "id": "8f1e2c3a-9b7d-4e21-9c31-2a5e6f7a8b90",
    "name": "CI key",
    "keyPrefix": "dsk_8f1e2c3a",
    "key": "dsk_8f1e2c3a9b7d4e219c312a5e6f7a8b90",
    "scopes": ["chat:write", "documents:read"],
    "status": "active",
    "lastUsedAt": null,
    "expiresAt": null,
    "createdAt": "2026-07-17T18:00:00.000Z"
  }
}
// "key" is only ever returned on this create call — store it now.`,
  },
  {
    title: "Chat",
    blurb:
      "Full CRUD over chat threads and their messages. DELETE soft-deletes the thread (trash). AI streaming responses remain on /api/chat/send.",
    endpoints: [
      { method: "GET", path: "/api/v1/chat/threads", description: "List threads (?status=, ?agentId=)" },
      { method: "POST", path: "/api/v1/chat/threads", description: 'Create — { "title", "initialMessage"?, "agentId"?, "topicId"?, "tags"? }' },
      { method: "GET", path: "/api/v1/chat/threads/:id", description: "Thread detail incl. messages" },
      { method: "PATCH", path: "/api/v1/chat/threads/:id", description: "Update title, tags, status, isArchived, …" },
      { method: "DELETE", path: "/api/v1/chat/threads/:id", description: "Soft-delete" },
      { method: "GET", path: "/api/v1/chat/threads/:id/messages", description: "List messages" },
      { method: "POST", path: "/api/v1/chat/threads/:id/messages", description: 'Append — { "role", "content" } or { "messages": [...] }' },
      { method: "PATCH", path: "/api/v1/chat/threads/:id/messages/:messageId", description: "Edit message content" },
      { method: "DELETE", path: "/api/v1/chat/threads/:id/messages/:messageId", description: "Delete a message" },
    ],
    example: `curl -X POST $BASE/chat/threads -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"title":"API thread","initialMessage":"Hello!"}'`,
    response: `{
  "data": {
    "id": 1815,
    "title": "API thread",
    "topic": null,
    "topicId": null,
    "tags": [],
    "agentId": null,
    "contextMode": "MAX",
    "chatMode": "normal",
    "status": "active",
    "isArchived": false,
    "includeInRag": true,
    "createdAt": "2026-07-17T20:41:00.031Z",
    "updatedAt": "2026-07-17T20:41:00.031Z",
    "linkedSpaceIds": [],
    "messages": [
      {
        "id": "29ae07bb-5ebe-49dc-b475-4f26a81bd9b6",
        "role": "user",
        "content": "Hello!",
        "timestamp": "2026-07-17T20:41:00.031Z"
      }
    ]
  }
}`,
  },
  {
    title: "Workspaces",
    blurb: "Team workspaces. Fields: name (required), slug.",
    endpoints: [
      { method: "GET", path: "/api/v1/workspaces", description: "List" },
      { method: "POST", path: "/api/v1/workspaces", description: "Create" },
      { method: "GET", path: "/api/v1/workspaces/:id", description: "Detail" },
      { method: "PATCH", path: "/api/v1/workspaces/:id", description: "Update" },
      { method: "DELETE", path: "/api/v1/workspaces/:id", description: "Delete" },
    ],
    example: `curl -X POST $BASE/workspaces -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Acme Inc"}'`,
    response: `{
  "data": {
    "id": "2f017b08-a4b6-4292-80e4-b121599303b7",
    "name": "Acme Inc",
    "slug": null,
    "createdAt": "2026-07-17T18:56:44.770Z",
    "updatedAt": "2026-07-17T18:56:44.770Z"
  }
}`,
  },
  {
    title: "Spaces",
    blurb:
      "Mastermind spaces. Fields: name (required), description, icon, color, goals[], notes, workspaceId, includeInRag. DELETE soft-deletes. Filter: ?workspaceId=.",
    endpoints: [
      { method: "GET", path: "/api/v1/spaces", description: "List" },
      { method: "POST", path: "/api/v1/spaces", description: "Create" },
      { method: "GET", path: "/api/v1/spaces/:id", description: "Detail" },
      { method: "PATCH", path: "/api/v1/spaces/:id", description: "Update" },
      { method: "DELETE", path: "/api/v1/spaces/:id", description: "Soft-delete" },
    ],
    example: `curl -X POST $BASE/spaces -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Product Launch","description":"Q3 launch plan"}'`,
    response: `{
  "data": {
    "id": "mkvo05r2c6ebeua",
    "name": "Product Launch",
    "description": "Q3 launch plan",
    "icon": null,
    "color": null,
    "goals": null,
    "notes": null,
    "workspaceId": null,
    "includeInRag": true,
    "createdAt": "2026-07-17T18:54:08.183Z",
    "updatedAt": "2026-07-17T18:54:08.183Z"
  }
}`,
  },
  {
    title: "Topics & Sub-topics",
    blurb:
      "A sub-topic is a topic with parentId set. Deleting a topic promotes its children to top-level. Filters: ?spaceId=, ?parentId=<id> or ?parentId=null.",
    endpoints: [
      { method: "GET", path: "/api/v1/topics", description: "List topics" },
      { method: "POST", path: "/api/v1/topics", description: 'Create — { "spaceId", "name", "parentId"? }' },
      { method: "GET", path: "/api/v1/topics/:id", description: "Detail" },
      { method: "PATCH", path: "/api/v1/topics/:id", description: "Update (set parentId null to promote)" },
      { method: "DELETE", path: "/api/v1/topics/:id", description: "Delete" },
      { method: "GET", path: "/api/v1/topics/:id/subtopics", description: "List sub-topics" },
      { method: "POST", path: "/api/v1/topics/:id/subtopics", description: "Create sub-topic under this topic" },
    ],
    example: `curl -X POST $BASE/topics -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"spaceId":"mkvo05r2c6ebeua","name":"Competitor research"}'`,
    response: `{
  "data": {
    "id": 444,
    "spaceId": "mkvo05r2c6ebeua",
    "name": "Competitor research",
    "description": null,
    "color": null,
    "icon": null,
    "parentId": null,
    "isSubtopic": false,
    "createdAt": "2026-07-17T20:41:54.336Z",
    "updatedAt": "2026-07-17T20:41:54.336Z"
  }
}`,
  },
  {
    title: "Documents",
    blurb:
      "Knowledge-base documents. Fields: title (required), type, content, spaceId, folderId, videoUrl, includeInRag, metadata. Filters: ?spaceId=, ?type=, ?folderId=.",
    endpoints: [
      { method: "GET", path: "/api/v1/documents", description: "List" },
      { method: "POST", path: "/api/v1/documents", description: "Create" },
      { method: "GET", path: "/api/v1/documents/:id", description: "Detail" },
      { method: "PATCH", path: "/api/v1/documents/:id", description: "Update" },
      { method: "DELETE", path: "/api/v1/documents/:id", description: "Delete" },
    ],
    example: `curl -X POST $BASE/documents -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"title":"Launch checklist","type":"note","content":"Draft the launch checklist"}'`,
    response: `{
  "data": {
    "id": "a1a56804-1743-4af2-82dc-223d8003d7e5",
    "title": "Launch checklist",
    "type": "note",
    "content": "Draft the launch checklist",
    "spaceId": null,
    "folderId": null,
    "videoUrl": null,
    "includeInRag": true,
    "metadata": null,
    "createdAt": "2026-07-17",
    "updatedAt": "2026-07-17"
  }
}`,
  },
  {
    title: "AI Agents",
    blurb:
      "Custom AI agents. Fields: name (required), description, category, icon, color, tags[], persona (role/expertise/communicationStyle/systemPrompt), preferredModel, customInstructions, capabilityModules[], isTemplate.",
    endpoints: [
      { method: "GET", path: "/api/v1/agents", description: "List (?category=)" },
      { method: "POST", path: "/api/v1/agents", description: "Create" },
      { method: "GET", path: "/api/v1/agents/:id", description: "Detail" },
      { method: "PATCH", path: "/api/v1/agents/:id", description: "Update" },
      { method: "DELETE", path: "/api/v1/agents/:id", description: "Delete" },
    ],
    example: `curl -X POST $BASE/agents -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Research Assistant","description":"Summarizes sources"}'`,
    response: `{
  "data": {
    "id": "73250bbe-566d-4c7a-a78d-b2ec387d36c2",
    "name": "Research Assistant",
    "description": "Summarizes sources",
    "category": "general",
    "icon": "Bot",
    "color": "purple",
    "tags": [],
    "persona": { "role": "", "expertise": [], "systemPrompt": "", "communicationStyle": "professional" },
    "preferredModel": null,
    "customInstructions": null,
    "capabilityModules": [],
    "isTemplate": false,
    "createdAt": "2026-07-17T18:53:55.753Z",
    "updatedAt": "2026-07-17T18:53:55.753Z"
  }
}`,
  },
  {
    title: "Plugins",
    blurb: "Install, manage, and uninstall plugins over REST.",
    endpoints: [
      { method: "GET", path: "/api/v1/plugins", description: "List installed plugins" },
      { method: "POST", path: "/api/v1/plugins", description: 'Install — { "zipUrl", "repo"?, "tag"? }' },
      { method: "GET", path: "/api/v1/plugins/:pluginId", description: "Detail incl. manifest" },
      { method: "PATCH", path: "/api/v1/plugins/:pluginId", description: "Set status active/inactive" },
      { method: "DELETE", path: "/api/v1/plugins/:pluginId", description: "Uninstall (removes data, secrets, routes)" },
    ],
    example: `curl -X POST $BASE/plugins -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"zipUrl":"https://github.com/you/plugin/releases/download/v1.0.0/plugin.zip","repo":"you/plugin","tag":"v1.0.0"}'`,
    response: `{
  "data": {
    "pluginId": "my-plugin",
    "name": "My Plugin",
    "version": "1.0.0",
    "status": "active",
    "sourceRepo": "you/plugin",
    "sourceTag": "v1.0.0",
    "installedAt": "2026-07-17T18:00:00.000Z",
    "updatedAt": "2026-07-17T18:00:00.000Z"
  }
}`,
  },
  {
    title: "Plugin-hosted API routes",
    blurb:
      'Plugins can host their own API routes through Deeply Smart. Register a route with a handler, then call it at /api/v1/plugins/:pluginId/api/<path>. Handler types: "static" (fixed JSON), "collection" (full CRUD over the plugin\'s record store — GET lists, GET with :key reads, POST creates, PUT/PATCH updates, DELETE removes), and "proxy" (forwards to an external https URL; header values may use {{secret:NAME}} and {{param:name}}). Path segments support :param captures, e.g. "todos/:id".',
    endpoints: [
      { method: "GET", path: "/api/v1/plugins/:pluginId/routes", description: "List registered routes" },
      { method: "POST", path: "/api/v1/plugins/:pluginId/routes", description: 'Register — { "method", "path", "handler", "description"? }' },
      { method: "GET", path: "/api/v1/plugins/:pluginId/routes/:routeId", description: "Route detail" },
      { method: "PATCH", path: "/api/v1/plugins/:pluginId/routes/:routeId", description: "Update route" },
      { method: "DELETE", path: "/api/v1/plugins/:pluginId/routes/:routeId", description: "Unregister" },
      { method: "ANY", path: "/api/v1/plugins/:pluginId/api/<path>", description: "Execute the matching hosted route" },
    ],
    example: `# Register a collection route, then use it\ncurl -X POST $BASE/plugins/my-plugin/routes -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"method":"POST","path":"todos","handler":{"type":"collection","collection":"todos"}}'\n\ncurl -X POST $BASE/plugins/my-plugin/api/todos -H "Authorization: Bearer $KEY" \\\n  -H "Content-Type: application/json" -d '{"data":{"text":"hello"}}'`,
    response: `// POST /plugins/my-plugin/routes → 201 (route registered)
{
  "data": {
    "id": "42c546a4-616a-44b3-b712-dea196d3f0f8",
    "pluginId": "my-plugin",
    "method": "POST",
    "path": "todos",
    "handler": { "type": "collection", "collection": "todos" },
    "description": null,
    "isActive": true,
    "createdAt": "2026-07-17T18:56:25.902Z",
    "updatedAt": "2026-07-17T18:56:25.902Z"
  }
}

// POST /plugins/my-plugin/api/todos → 201 (route executed)
{
  "data": {
    "key": "3f9c9e2a-2b7a-4b7e-8f4e-1a9b6c5d4e3f",
    "data": { "text": "hello" },
    "created_at": "2026-07-17T18:57:00.000Z",
    "updated_at": "2026-07-17T18:57:00.000Z"
  }
}`,
  },
];

const METHOD_COLORS: Record<string, string> = {
  GET: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  POST: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  PATCH: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  PUT: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  DELETE: "bg-red-500/15 text-red-600 dark:text-red-400",
  ANY: "bg-purple-500/15 text-purple-600 dark:text-purple-400",
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function DeveloperApi() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [preset, setPreset] = useState("full");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const keysQuery = useQuery({
    queryKey: ["v1-api-keys"],
    queryFn: () => v1Fetch<{ data: ApiKeyRecord[] }>("/keys").then((r) => r.data),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["v1-api-keys"] });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; scopes: string[] }) =>
      v1Fetch<{ data: ApiKeyRecord & { key: string } }>("/keys", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (result) => {
      setCreateOpen(false);
      setNewName("");
      setCreatedKey(result.data.key);
      setCopied(false);
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Failed to create key", description: e.message, variant: "destructive" }),
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      v1Fetch(`/keys/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => v1Fetch(`/keys/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast({ title: "API key deleted" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });

  const copyCreatedKey = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey);
    setCopied(true);
    toast({ title: "Copied to clipboard" });
  };

  return (
    <div className="container mx-auto max-w-5xl p-4 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <KeyRound className="h-6 w-6" /> Developer API
        </h1>
        <p className="text-muted-foreground mt-1">
          REST API v1 — manage API keys and browse the endpoint reference. Base URL:{" "}
          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{`${window.location.origin}/api/v1`}</code>
        </p>
      </div>

      <Tabs defaultValue="keys">
        <TabsList>
          <TabsTrigger value="keys">API Keys</TabsTrigger>
          <TabsTrigger value="docs">Documentation</TabsTrigger>
        </TabsList>

        {/* ----------------------------- Keys tab ----------------------------- */}
        <TabsContent value="keys" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Keys authenticate requests to <code className="bg-muted px-1 rounded">/api/v1</code>. The full key is
              shown once at creation — store it securely.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => keysQuery.refetch()}>
                <RefreshCcw className="h-4 w-4" />
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1" /> Create key
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              {keysQuery.isLoading ? (
                <p className="p-6 text-sm text-muted-foreground">Loading keys…</p>
              ) : keysQuery.isError ? (
                <p className="p-6 text-sm text-destructive">
                  {(keysQuery.error as Error)?.message ?? "Failed to load keys"}
                </p>
              ) : (keysQuery.data ?? []).length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">
                  No API keys yet. Create one to start using the REST API.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead>Scopes</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last used</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(keysQuery.data ?? []).map((key) => (
                      <TableRow key={key.id}>
                        <TableCell className="font-medium">{key.name}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{key.keyPrefix}…</code>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-56">
                            {key.scopes.map((s) => (
                              <Badge key={s} variant="secondary" className="text-[10px]">
                                {s}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={key.status === "active" ? "default" : "destructive"}>{key.status}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {key.lastUsedAt
                            ? formatDistanceToNow(new Date(key.lastUsedAt), { addSuffix: true })
                            : "never"}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  patchMutation.mutate({
                                    id: key.id,
                                    patch: { status: key.status === "active" ? "revoked" : "active" },
                                  })
                                }
                              >
                                <ShieldOff className="h-4 w-4 mr-2" />
                                {key.status === "active" ? "Revoke" : "Reactivate"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => deleteMutation.mutate(key.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ----------------------------- Docs tab ----------------------------- */}
        <TabsContent value="docs" className="space-y-4">
          {DOCS.map((doc) => (
            <Card key={doc.title}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{doc.title}</CardTitle>
                <CardDescription className="whitespace-pre-wrap">{doc.blurb}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  {doc.endpoints.map((ep) => (
                    <div key={`${ep.method} ${ep.path}`} className="flex flex-wrap items-baseline gap-2 text-sm">
                      <span
                        className={`inline-block w-16 text-center text-[11px] font-bold rounded px-1 py-0.5 ${METHOD_COLORS[ep.method] ?? "bg-muted"}`}
                      >
                        {ep.method}
                      </span>
                      <code className="text-xs">{ep.path}</code>
                      <span className="text-xs text-muted-foreground">— {ep.description}</span>
                    </div>
                  ))}
                </div>
                {doc.example && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Example request</p>
                    <pre className="bg-muted rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                      {doc.example}
                    </pre>
                  </div>
                )}
                {doc.response && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Example response</p>
                    <pre className="bg-muted rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                      {doc.response}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>The key is shown once after creation — copy it right away.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Name</Label>
              <Input
                id="key-name"
                placeholder="e.g. Zapier integration"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Access</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SCOPE_PRESETS).map(([value, p]) => (
                    <SelectItem key={value} value={value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Scopes: {SCOPE_PRESETS[preset].scopes.join(", ")}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!newName.trim() || createMutation.isPending}
              onClick={() =>
                createMutation.mutate({ name: newName.trim(), scopes: SCOPE_PRESETS[preset].scopes })
              }
            >
              {createMutation.isPending ? "Creating…" : "Create key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show-once key dialog */}
      <Dialog open={createdKey !== null} onOpenChange={(open) => !open && setCreatedKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API key created</DialogTitle>
            <DialogDescription>
              Copy this key now — for security it cannot be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-muted rounded-md p-3 text-xs break-all">{createdKey}</code>
            <Button variant="outline" size="icon" onClick={copyCreatedKey}>
              {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedKey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
